// @flow

import { all, put, takeEvery, call } from "redux-saga/effects";
import type { Action } from "redux-action";
import http from "@http";
import actionCreators from "@modules/service/actionCreator";
import type {
  DefaultStandardScriptsResponse,
  DefaultStandardScript,
} from "@modules/service/reducer/default-standard-scripts/types";
import type { ApprovableContainer } from "@modules/service/types";
import { push } from "connected-react-router";
import { route } from "@routes/helper";
import {
  GetDefaultStandardScripts,
  GetDefaultStandardScript,
  UpdateDefaultStandardScript,
} from "@modules/service/constants/actions";
import { sendSuccessMessage } from "@modules/app/actionCreator";

type DefaultStandardScriptsRequest = $Exact<{
  clientId: string,
}>;

type ItemResponse<T> = $Exact<T>;

export type DefaultStandardScriptRequest = $Exact<{
  ...DefaultStandardScriptsRequest,
  stableId: string,
}>;

type DefaultStandardScriptUpdateRequest = $Exact<{
  ...DefaultStandardScriptRequest,
  draft: DefaultStandardScript,
}>;

export function* onGetDefaultStandardScripts({
  payload,
}: Action<DefaultStandardScriptsRequest>): Generator<
  *,
  void,
  { data: DefaultStandardScriptsResponse },
> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/standard-script/default-script`,
    );

    yield put(actionCreators.GetDefaultStandardScriptsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetDefaultStandardScriptsError(e.response));
  }
}

export function* onGetDefaultStandardScript({
  payload,
}: Action<DefaultStandardScriptRequest>): Generator<*, void, { data: DefaultStandardScript }> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/standard-script/default-script/${payload.stableId}`,
    );

    yield put(actionCreators.GetDefaultStandardScriptSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetDefaultStandardScriptError(e.response));
  }
}

export function* onUpdateDefaultStandardScript({
  payload,
}: Action<DefaultStandardScriptUpdateRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<DefaultStandardScript>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.stableId || !payload.draft) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.put,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/standard-script/default-script/${payload.stableId}`,
      payload.draft,
    );

    yield all([
      put(actionCreators.UpdateDefaultStandardScriptSuccess(response.data)),
      put(actionCreators.ToggleEditDefaultStandardScriptModal(false)),
      put(actionCreators.GetDefaultStandardScripts({ clientId: payload.clientId })),
      put(push(route("ADMINISTRATION_CLIENT_FEATURES_PATH", { clientId: payload.clientId }))),
      put(sendSuccessMessage("DRAFT_UPDATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateDefaultStandardScriptError(e.response));
  }
}

export function* defaultStandardScriptsSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(GetDefaultStandardScripts, onGetDefaultStandardScripts),
    takeEvery(GetDefaultStandardScript, onGetDefaultStandardScript),
    takeEvery(UpdateDefaultStandardScript, onUpdateDefaultStandardScript),
  ]);
}

export default defaultStandardScriptsSagas;
